/**
 * src/hooks/useApprovals.ts
 * React hook for managing approval requests state, pending count badges,
 * regular user submissions, and admin approvals/rejections.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, ApprovalItem } from '../services/api';
import { useToast } from '../context/ToastContext';

export function useApprovals(initialStatus: string = 'pending') {
  const [requests, setRequests] = useState<ApprovalItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [error, setError] = useState<string | null>(null);

  const { showToast } = useToast();

  // Fetch pending count for badges
  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await api.approvals.count();
      setPendingCount(res.pendingCount);
      return res.pendingCount;
    } catch (err: any) {
      console.warn('Could not fetch pending approvals count:', err.message);
      return 0;
    }
  }, []);

  // Fetch approvals list
  const fetchApprovals = useCallback(async (status: string = statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.approvals.list({ status });
      setRequests(res.requests);
      setTotal(res.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load approval requests');
      showToast(err.message || 'Failed to load approval requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  // Submit request (for users)
  const submitRequest = async (data: {
    transaction_type: 'paid' | 'receive';
    amount: number;
    description: string;
    category_name?: string;
    transaction_date?: string;
    receipt_url?: string;
    user_id?: number;
  }) => {
    setSubmitting(true);
    try {
      const created = await api.approvals.create(data);
      showToast('Entry submitted! It is now Under Review by Admin.', 'success');
      // Refresh count and list
      fetchPendingCount();
      if (statusFilter === 'pending' || statusFilter === 'all') {
        fetchApprovals(statusFilter);
      }
      return created;
    } catch (err: any) {
      showToast(err.message || 'Failed to submit entry request', 'error');
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  // Review request (for admins: approve / reject)
  const reviewRequest = async (id: number, decision: 'approve' | 'reject', review_note?: string) => {
    try {
      const res = await api.approvals.review(id, { decision, review_note });
      showToast(
        decision === 'approve'
          ? 'Transaction approved and committed to ledger!'
          : 'Request rejected.',
        decision === 'approve' ? 'success' : 'info'
      );

      // Optimistic update
      setRequests(prev => prev.filter(r => r.id !== id));
      setPendingCount(prev => Math.max(0, prev - 1));

      // Refresh data
      fetchPendingCount();
      fetchApprovals(statusFilter);
      return res;
    } catch (err: any) {
      showToast(err.message || 'Failed to process review', 'error');
      throw err;
    }
  };

  useEffect(() => {
    fetchApprovals(statusFilter);
    fetchPendingCount();
  }, [statusFilter, fetchApprovals, fetchPendingCount]);

  return {
    requests,
    total,
    pendingCount,
    loading,
    submitting,
    statusFilter,
    setStatusFilter,
    error,
    refresh: () => {
      fetchApprovals(statusFilter);
      fetchPendingCount();
    },
    fetchPendingCount,
    submitRequest,
    reviewRequest,
  };
}
